/**
 * Shared status-variant tokens. The canonical class map lives here so that
 * both `@quovy/field-types` (which cannot depend on `@quovy/ui`) and
 * `@quovy/ui`'s `StatusBadge` render identical pills. Tailwind utility
 * classes only — no React, no `cva`, no runtime deps.
 */

export type StatusVariant =
  | "info"
  | "success"
  | "warning"
  | "error"
  | "neutral"
  | "queued"
  | "running"
  | "waiting"
  | "succeeded"
  | "failed"
  | "canceled"
  | "needs-review"
  | "edited"
  | "locked";

export const STATUS_VARIANT_CLASS: Record<StatusVariant, string> = {
  info: "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400",
  success: "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400",
  // Yellow-400 on yellow-900/30 is below readable contrast in dark mode —
  // cream fill + gold type. 950/50 + 200 keeps the warning hue and matches
  // the green/red score pills sitting next to it.
  warning: "bg-yellow-100 dark:bg-yellow-950/50 text-yellow-800 dark:text-yellow-200",
  error: "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400",
  neutral: "bg-muted/50 text-foreground",
  queued: "bg-yellow-100 dark:bg-yellow-950/50 text-yellow-800 dark:text-yellow-200",
  running: "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400",
  waiting: "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400",
  succeeded: "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400",
  failed: "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400",
  canceled: "bg-muted/50 text-foreground",
  "needs-review": "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400",
  edited: "bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400",
  locked: "bg-slate-200 dark:bg-slate-800/60 text-slate-800 dark:text-slate-300",
};
